import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { Loader2, ShoppingBag, Wrench, Mail, Phone, MapPin, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function ReviewPanel({ k, onSaved }) {
  const locked = !!k.legacy_order;
  const [lines, setLines] = useState(k.lines || []);
  const [notes, setNotes] = useState(k.review_notes || "");
  const [biz, setBiz] = useState(k.business_name || "");
  const [abn, setAbn] = useState(k.abn || "");
  const [saving, setSaving] = useState(null);
  const setLine = (i, field, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [field]: Math.max(0, Number(v) || 0) } : l)));
  const save = async (status) => {
    setSaving(status || "save");
    try {
      const body = { review_notes: notes, business_name: biz, abn };
      if (status) body.status = status;
      if (!locked) body.lines = lines;
      const { data } = await api.patch(`/kit-builds/${k.id}`, body);
      onSaved(data);
      toast.success(status ? `Kit marked “${status}”` : "Review saved");
    } catch (e) { toast.error(formatApiError(e, "Could not save review")); }
    finally { setSaving(null); }
  };
  const total = lines.reduce((s, l) => s + l.qty * l.unit_launch, 0);
  return (
    <div className="mt-4 pt-4 border-t border-ei-mist" data-testid={`kit-review-${k.id}`}>
      <div className="text-xs font-semibold text-ei-graphite/60 uppercase tracking-wide">Technical review</div>
      {!locked && (
        <div className="mt-2 space-y-1.5">
          {lines.map((l, i) => (
            <div key={l.sku + i} className="grid grid-cols-12 gap-2 items-center text-sm">
              <span className="col-span-6 truncate text-ei-graphite/85">{l.name}</span>
              <input type="number" min="0" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="col-span-2 rounded-sm border border-ei-mist px-2 py-1 text-sm tnum" data-testid={`kit-line-qty-${k.id}-${i}`} aria-label="Quantity" />
              <input type="number" min="0" step="0.01" value={l.unit_launch} onChange={(e) => setLine(i, "unit_launch", e.target.value)} className="col-span-3 rounded-sm border border-ei-mist px-2 py-1 text-sm tnum" data-testid={`kit-line-price-${k.id}-${i}`} aria-label="Unit price ex GST" />
              <span className="col-span-1 text-right tnum text-xs text-ei-graphite/70">{money(l.qty * l.unit_launch)}</span>
            </div>
          ))}
          <div className="text-right text-xs text-ei-graphite/70">Adjusted total <strong className="text-ei-navy tnum">{money(total)}</strong> + GST</div>
        </div>
      )}
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <input value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="Business name (for Xero contact)" className="rounded-sm border border-ei-mist px-3 py-2 text-sm" data-testid={`kit-biz-${k.id}`} />
        <input value={abn} onChange={(e) => setAbn(e.target.value)} placeholder="ABN (for Xero contact)" className="rounded-sm border border-ei-mist px-3 py-2 text-sm tnum" data-testid={`kit-abn-${k.id}`} />
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Review notes — phase check, roof, string sizing, substitutions…" className="mt-2 w-full rounded-sm border border-ei-mist px-3 py-2 text-sm" data-testid={`kit-notes-${k.id}`} />
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={() => save()} disabled={!!saving} className="font-display text-xs font-bold text-ei-navy border border-ei-navy/25 px-3 py-2 rounded-sm hover:border-ei-navy min-h-[40px] disabled:opacity-60" data-testid={`kit-save-${k.id}`}>Save notes</button>
        {!locked && (
          <>
            <button onClick={() => save("Approved")} disabled={!!saving} className="inline-flex items-center gap-1.5 font-display text-xs font-bold bg-emerald-700 text-white px-3 py-2 rounded-sm hover:bg-emerald-800 min-h-[40px] disabled:opacity-60" data-testid={`kit-approve-${k.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
            <button onClick={() => save("Needs changes")} disabled={!!saving} className="inline-flex items-center gap-1.5 font-display text-xs font-bold bg-amber-600 text-white px-3 py-2 rounded-sm hover:bg-amber-700 min-h-[40px] disabled:opacity-60" data-testid={`kit-needs-changes-${k.id}`}><AlertTriangle className="w-3.5 h-3.5" /> Needs changes</button>
          </>
        )}
      </div>
      {k.review_log?.length > 0 && (
        <ul className="mt-3 text-[11px] text-ei-graphite/60 space-y-0.5">
          {k.review_log.slice(-3).reverse().map((r) => <li key={r.at}>{new Date(r.at).toLocaleString("en-AU")} · {r.by}{r.status ? ` · ${r.status}` : ""}{r.adjusted ? " · lines adjusted" : ""}{r.notes ? ` — ${r.notes}` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}

function KitCard({ k, onDraft, busy, onSaved, onDelete, selected, onToggle }) {
  const sh = k.legacy_order;
  return (
    <div className="bg-white border border-ei-mist rounded-md p-5" data-testid={`kit-build-${k.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {!sh && <input type="checkbox" checked={selected} onChange={() => onToggle(k.id)} className="w-4 h-4 accent-ei-navy cursor-pointer" aria-label={`Select kit ${k.reference}`} data-testid={`kit-select-${k.id}`} />}
            <span className="font-semibold text-ei-navy">{k.kit_label}</span>
            <span className="text-xs tnum text-ei-graphite/60">{k.reference}</span>
            <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-sm border ${sh?.paid_at ? "bg-emerald-50 text-emerald-800 border-emerald-200" : sh ? "bg-amber-50 text-amber-800 border-amber-200" : k.status === "Approved" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : k.status === "Needs changes" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-ei-mist/50 text-ei-navy border-ei-mist"}`} data-testid={`kit-status-${k.id}`}>
              {sh?.paid_at ? 'Historical payment — review required' : k.status}
            </span>
          </div>
          <div className="mt-1 text-xs text-ei-graphite/60">{k.phase} phase · {k.battery_kwh} kWh battery · {k.solar_kw} kW solar · {k.inverter}</div>
          <ul className="mt-3 text-sm text-ei-graphite/85 space-y-0.5">
            {(k.lines || []).filter((l) => l.qty > 0).map((l) => (
              <li key={l.sku + l.name} className="flex justify-between gap-4"><span>{l.qty} × {l.name}</span><span className="tnum">{l.price_status === 'inclusion_review' ? 'Inclusion to confirm' : l.price_status === 'carton_listed' ? 'Carton list · verify' : l.price_status === 'to_quote' ? 'To be quoted' : l.price_status === 'included' ? 'Included' : money(l.qty * l.unit_launch)}</span></li>
            ))}
          </ul>
          <div className="mt-2 font-display font-bold text-ei-navy tnum" data-testid={`kit-known-total-${k.id}`}>{money(k.launch_total)} <span className="text-xs font-normal text-ei-graphite/60">{k.system_snapshot ? '+ GST · known items only, not a complete quote' : `+ GST · saves ${money(k.saved)}`}</span></div>
          {k.system_snapshot && <details className="mt-4 border border-ei-mist rounded p-3 text-xs leading-relaxed" data-testid={`system-snapshot-${k.id}`}><summary className="cursor-pointer font-semibold" data-testid={`system-snapshot-toggle-${k.id}`}>Original configuration & required accessories (immutable)</summary><p className="my-2">{k.system_snapshot.selection.kind === 'fixed' ? `Fixed pack: ${k.system_snapshot.selection.pack_id}. No customer substitutions.` : 'Custom configuration'} · Catalogue {k.system_snapshot.catalogue_version}</p><ul>{k.system_snapshot.lines.map((line,i) => <li key={`${line.name}-${i}`} data-testid={`system-snapshot-line-${k.id}-${i}`} className="my-2">{line.quantity} × {line.name}<br/><span className="text-ei-graphite/70">{line.note} {['to_quote','inclusion_review'].includes(line.price_status) ? 'Price / inclusion / exact specification outstanding.' : ''}</span></li>)}</ul><p>Technical status: {k.system_snapshot.status}. Exact matching, cable/accessory schedule and pricing must be reviewed before a quote is released.</p></details>}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ei-graphite/70">
            <span className="font-semibold text-ei-navy">{k.contact_name}</span>
            <a href={`mailto:${k.email}`} className="inline-flex items-center gap-1 hover:text-ei-navy"><Mail className="w-3 h-3" /> {k.email}</a>
            <a href={`tel:${k.phone}`} className="inline-flex items-center gap-1 hover:text-ei-navy"><Phone className="w-3 h-3" /> {k.phone}</a>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {k.location}</span>
          </div>
          {k.notes && <p className="mt-2 text-xs text-ei-graphite/70 italic">“{k.notes}”</p>}
        </div>
        <div className="flex flex-col gap-2 lg:items-end shrink-0">
          {!sh ? (
            <>
              <button onClick={() => onDraft(k.id)} disabled={busy === k.id || k.status !== "Approved"} title={k.status !== "Approved" ? "Approve the kit first" : ""} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink transition-colors min-h-[44px] disabled:opacity-50" data-testid={`kit-draft-${k.id}`}>
                {busy === k.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Create reviewed quote
              </button>
              {k.status !== "Approved" && <span className="text-[11px] text-ei-graphite/55">Approve in technical review to enable</span>}
            </>
          ) : (
            <>
              <span className="text-xs text-ei-graphite/60">Draft {sh.name} · total {sh.currency || "AUD"} {sh.total}</span>
              <span className="text-xs text-amber-800" data-testid={`kit-legacy-review-${k.id}`}>Historical external order record — payment requires reconciliation. No payment link is active.</span>
            </>
          )}
          {!sh && (
            <button onClick={() => onDelete(k)} disabled={busy === k.id} className="inline-flex items-center gap-2 font-display text-xs font-bold text-red-700 border border-red-200 px-3 py-2 rounded-sm hover:bg-red-50 min-h-[40px] disabled:opacity-50" data-testid={`kit-delete-${k.id}`}>
              <Trash2 className="w-3.5 h-3.5" /> Delete kit
            </button>
          )}
        </div>
      </div>
      <ReviewPanel key={k.updated_at} k={k} onSaved={onSaved} />
    </div>
  );
}

export default function KitBuilds() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = () => api.get("/kit-builds").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e, "Could not load kit builds")));
  useEffect(() => { load(); }, []);

  const createDraft = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/kit-builds/${id}/quote`);
      toast.success(data.replayed ? 'Opening existing kit quote' : 'Kit quote created for staff release');
      navigate(`/staff/enquiries/${data.enquiry_id}`);
    } catch (e) { toast.error(formatApiError(e, "Could not create draft order")); }
    finally { setBusy(null); }
  };

  const removeKit = async (k) => {
    if (!window.confirm(`Permanently delete kit ${k.reference} (${k.kit_label})? This cannot be undone.`)) return;
    setBusy(k.id);
    try {
      await api.delete(`/kit-builds/${k.id}`);
      toast.success(`Kit ${k.reference} deleted`);
      setItems((prev) => prev.filter((x) => x.id !== k.id));
    } catch (e) { toast.error(formatApiError(e, "Could not delete this kit")); }
    finally { setBusy(null); }
  };

  const deletable = (items || []).filter((k) => !k.legacy_order);
  const allSelected = deletable.length > 0 && selected.length === deletable.length;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : deletable.map((k) => k.id));

  const deleteSelected = async () => {
    if (!window.confirm(`Permanently delete ${selected.length} kit${selected.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    const failed = [];
    for (const id of selected) {
      try { await api.delete(`/kit-builds/${id}`); } catch { failed.push(id); }
    }
    const removed = selected.filter((id) => !failed.includes(id));
    setItems((prev) => prev.filter((x) => !removed.includes(x.id)));
    setSelected(failed);
    setBulkBusy(false);
    if (failed.length) toast.error(`${removed.length} deleted · ${failed.length} could not be deleted`);
    else toast.success(`${removed.length} kit${removed.length === 1 ? "" : "s"} deleted`);
  };

  return (
    <div>
      <Seo title="Kit builds — EI Battery Workspace" />
      <span className="ei-eyebrow">Build a kit</span>
      <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Kit builds</h1>
      <p className="mt-2 text-sm text-ei-graphite/70 max-w-2xl">Review the contractor's kit, then create its quote in the existing quote builder. Staff release, customer acceptance and confirmed stock are required before payment.</p>
      {deletable.length > 0 && (
        <div className="mt-5 flex flex-wrap items-center gap-3 bg-white border border-ei-mist rounded-md px-4 py-3" data-testid="kit-bulk-bar">
          <label className="inline-flex items-center gap-2 text-sm text-ei-navy cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-ei-navy cursor-pointer" data-testid="kit-select-all" />
            Select all kits
          </label>
          <span className="text-xs text-ei-graphite/60 tnum" data-testid="kit-selected-count">{selected.length} selected</span>
          <button onClick={deleteSelected} disabled={!selected.length || bulkBusy} className="inline-flex items-center gap-2 font-display text-xs font-bold text-red-700 border border-red-200 px-3 py-2 rounded-sm hover:bg-red-50 min-h-[40px] disabled:opacity-40 disabled:hover:bg-transparent ml-auto" data-testid="kit-delete-selected">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete selected
          </button>
        </div>
      )}
      <div className="mt-6 space-y-4" data-testid="kit-builds-list">
        {items === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-ei-navy" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-ei-mist rounded-md p-16 text-center text-ei-graphite/60" data-testid="kit-builds-empty">
            <Wrench className="w-12 h-12 mx-auto text-ei-mist" />
            <p className="mt-4 font-semibold text-ei-navy">No kit builds yet</p>
          </div>
        ) : items.map((k) => <KitCard key={k.id} k={k} onDraft={createDraft} busy={busy} onDelete={removeKit} selected={selected.includes(k.id)} onToggle={toggle} onSaved={(kit) => setItems((prev) => prev.map((x) => (x.id === kit.id ? kit : x)))} />)}
      </div>
    </div>
  );
}
